import type { Analysis } from '../../types/models';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface TechnicalAnalysisProps {
  analysis: Analysis;
}



interface DisplayTechnicalSkill {
  skill: string;
  proficiency: 'EXPERT' | 'ADVANCED' | 'INTERMEDIATE' | 'BASIC' | 'BEGINNER';
  evidence: string;
  currency: string;
}

// Helper to map proficiency to Badge variant and color
const getProficiencyProps = (proficiency: DisplayTechnicalSkill['proficiency']) => {
  switch (proficiency) {
    case 'EXPERT':
      return { variant: "default" as const, className: "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 text-white" };
    case 'ADVANCED':
      return { variant: "default" as const, className: "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white" };
    case 'INTERMEDIATE':
      return { variant: "default" as const, className: "bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-400 dark:hover:bg-yellow-500 text-black" };
    case 'BASIC':
      return { variant: "secondary" as const, className: "" };
    case 'BEGINNER':
      return { variant: "outline" as const, className: "" };
    default:
      return { variant: "secondary" as const, className: "" };
  }
};

export function TechnicalAnalysis({ analysis }: TechnicalAnalysisProps) {

  const processSkills = (skillsData: any[]): DisplayTechnicalSkill[] => {
    if (!Array.isArray(skillsData)) {
      console.warn('Technical skills data is not an array:', skillsData);
      return [];
    }

    return skillsData.map((skill: any, index: number): DisplayTechnicalSkill => {
      // Ensure we have a valid skill object
      if (!skill || (typeof skill !== 'object' && typeof skill !== 'string')) {
        console.warn(`Invalid skill data at index ${index}:`, skill);
        return {
          skill: 'Invalid Skill Data',
          proficiency: 'BEGINNER',
          evidence: 'Unable to parse skill information',
          currency: 'Unknown'
        };
      }

      // Handle string skills (legacy format)
      if (typeof skill === 'string') {
        return {
          skill: skill.trim(),
          proficiency: 'INTERMEDIATE',
          evidence: 'Skill mentioned in analysis',
          currency: 'Recent'
        };
      }

      // Extract skill information from object
      const skillName = skill.skill || skill.name || 'Unknown Skill';
      const evidence = skill.evidence || skill.assessment || skill.description || 'No evidence provided';
      const currency = skill.currency || skill.experience || skill.years || 'Recent';
      
      // Validate and normalize proficiency
      let proficiency: DisplayTechnicalSkill['proficiency'] = 'INTERMEDIATE';
      
      if (skill.proficiency) {
        const proficiencyStr = skill.proficiency.toString().toUpperCase();
        const validProficiencies: DisplayTechnicalSkill['proficiency'][] = ['EXPERT', 'ADVANCED', 'INTERMEDIATE', 'BASIC', 'BEGINNER'];
        
        if (validProficiencies.includes(proficiencyStr as DisplayTechnicalSkill['proficiency'])) {
          proficiency = proficiencyStr as DisplayTechnicalSkill['proficiency'];
        } else {
          // Try to infer proficiency from evidence text if provided proficiency is invalid
          const evidenceText = evidence.toLowerCase();
          if (evidenceText.includes('expert') || evidenceText.includes('senior') || evidenceText.includes('lead')) {
            proficiency = 'EXPERT';
          } else if (evidenceText.includes('advanced') || evidenceText.includes('experienced')) {
            proficiency = 'ADVANCED';
          } else if (evidenceText.includes('basic') || evidenceText.includes('learning') || evidenceText.includes('beginner')) {
            proficiency = 'BASIC';
          }
          
          console.warn(`Invalid proficiency "${skill.proficiency}" for skill "${skillName}", inferred as ${proficiency}`);
        }
      }
      
      return {
        skill: skillName.trim(),
        proficiency,
        evidence: evidence.trim(),
        currency: currency.trim()
      };
    }).filter(skill => skill.skill !== 'Invalid Skill Data'); // Remove invalid entries
  };

  const skills: DisplayTechnicalSkill[] = Array.isArray(analysis.technical_skills) 
    ? processSkills(analysis.technical_skills)
    : [];

  if (!skills || skills.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">Technical skills analysis not available.</p>
    );
  }

  return (
    <div className="space-y-4">
      {skills.map((skill, index) => {
         const { variant, className } = getProficiencyProps(skill.proficiency);
         return (
           <Card key={index}>
             <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
               <CardTitle className="text-base font-medium text-foreground">{skill.skill}</CardTitle>
               <Badge variant={variant} className={cn(className)}>{skill.proficiency}</Badge>
             </CardHeader>
             <CardContent className="space-y-2 text-sm text-muted-foreground">
               <div>
                  <span className="font-medium text-foreground/90">Evidence: </span>
                  {skill.evidence}
               </div>
               <div>
                  <span className="font-medium text-foreground/90">Experience: </span>
                  {skill.currency}
               </div>
             </CardContent>
           </Card>
         );
      })}
    </div>
  );
}